import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HttpService } from 'src/app/http.service';

@Component({
  selector: 'app-dashboard-withdrawals', standalone: true,
  imports: [CommonModule, FormsModule],
  styleUrls: ['../dashboard/dashboard.component.scss'],
  template: `
    <section class="dash-card" *ngIf="allowed">
      <div class="dash-card-header"><h2>Top 10 Withdrawals</h2></div>
      <div class="p-3">
        <form class="d-flex flex-wrap align-items-end gap-2 mb-3" (ngSubmit)="applyRange()">
          <label>From<input class="form-control" type="date" name="withdrawFrom" aria-label="Withdrawals From" [(ngModel)]="dateFrom" required></label>
          <label>To<input class="form-control" type="date" name="withdrawTo" aria-label="Withdrawals To" [(ngModel)]="dateTo" required></label>
          <button class="btn btn-primary" type="submit">Apply</button>
        </form>
        <label>Match by <select [(ngModel)]="mode" (ngModelChange)="load()" aria-label="Withdrawal matching">
          <option value="phone">Mobile number</option><option value="bank">Bank account number</option>
        </select></label>
        <p class="small text-muted">{{appliedFrom}} – {{appliedTo}} (inclusive) · {{consolidated ? 'Consolidated report group' : 'Current company'}} · {{currency}} · UTC+08:00</p>
        <p class="small text-muted">Totals include posted withdrawals from players sharing the selected saved number.
          <span *ngIf="mode === 'bank'">A player with multiple bank accounts can appear in multiple groups; groups must not be added together.</span>
        </p>
        <p *ngIf="loading" role="status">Loading withdrawals…</p>
        <p *ngIf="error" role="alert">{{error}}</p>
      </div>
      <div class="dash-table-wrap" *ngIf="!loading && !error">
        <table class="dash-table"><thead><tr><th>Rank</th><th>{{mode === 'phone' ? 'Mobile number' : 'Bank account number'}}</th><th>Players</th><th>Withdrawals</th><th class="col-num">Total {{currency}}</th></tr></thead>
          <tbody><tr *ngFor="let row of rows; let i = index"><td>{{i+1}}</td><td>{{row.match}}</td>
            <td><details><summary>{{row.players.length}} matched player(s)</summary><div *ngFor="let player of row.players">{{player.company}} · {{player.code}} · {{player.name}} — {{player.withdraw | number:'1.2-2'}}</div></details></td>
            <td>{{row.count}}</td><td class="col-num">{{row.withdraw | number:'1.2-2'}}</td></tr>
            <tr *ngIf="!rows.length"><td colspan="5" class="state-cell">No withdrawals for this date range.</td></tr>
          </tbody></table>
      </div>
    </section>`
})
export class DashboardWithdrawalsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() date = '';
  @Input() consolidated = false;
  @Input() refreshKey: any;
  mode = 'phone'; rows: any[] = []; currency = ''; loading = false; error = '';
  dateFrom = ''; dateTo = ''; appliedFrom = ''; appliedTo = '';
  private customRange = false;
  private subscription?: Subscription;
  private ready = false;
  private request = 0;
  allowed = false;
  constructor(private http: HttpService) {}
  ngOnInit(): void {
    this.subscription = this.http.getSelectedCompany().subscribe(company => {
      this.ready = !!company;
      this.allowed = false;
      this.rows = [];
      this.currency = '';
      this.request++;
      if (this.ready) void this.load();
    });
  }
  ngOnChanges(): void {
    if (!this.customRange) this.dateFrom = this.dateTo = this.date;
    if (this.ready && !this.refreshKey) void this.load();
  }
  applyRange(): void { this.customRange = true; void this.load(); }
  ngOnDestroy(): void { this.request++; this.subscription?.unsubscribe(); }
  async load(): Promise<void> {
    const request = ++this.request;
    const company = this.http.getStaticSelectedCompany();
    if (!this.ready || !company) return;
    this.loading = true; this.error = ''; this.rows = [];
    try {
      // The old dashboard remains mounted while the switcher loads the new
      // company's menus. Never reuse the previous company's permission here.
      const grants: any = await this.http.post('/admin/user/permission/hidden', { company_uuid: company });
      if (request !== this.request || company !== this.http.getStaticSelectedCompany()) return;
      this.allowed = grants?.[this.consolidated ? 'consolidated-dashboard' : 'dashboard'] == 1;
      if (!this.allowed) return;
      if (!this.dateFrom || !this.dateTo) throw Error('Select both From and To dates.');
      if (this.dateFrom > this.dateTo) throw Error('From date must not be after To date.');
      const from = this.dateFrom, to = this.dateTo;
      const result: any = await this.http.post('/admin/company/dashboard/top-withdrawals', {
        company_uuid: company, date_from: from, date_to: to, consolidated: this.consolidated, match_by: this.mode
      });
      if (request !== this.request || company !== this.http.getStaticSelectedCompany()) return;
      if (!result?.status) throw Error(result?.message || 'Unable to load withdrawals.');
      this.rows = result.rows; this.currency = result.currency;
      this.appliedFrom = from; this.appliedTo = to;
    } catch (error: any) {
      if (request === this.request) this.error = error.message || 'Unable to load withdrawals.';
    } finally { if (request === this.request) this.loading = false; }
  }
}
